/* site/content/partnerAccount.js — the copy of lhc-site/login.html (2026-09-07
 * redesign): the partner sign-in + "Apply for a partner account" page. Every
 * string here is CMS-editable through the superadmin "Website" editor.
 *
 * NOTE ON HONESTY: the prototype ends each pane with
 *   "authentication is not connected — for design review only."
 *   "application is not connected — for design review only, nothing is submitted."
 * Both are FALSE on the platform — this page is the real sign-in (POST
 * /api/auth/login) and the real partner application (POST /api/auth/signup,
 * reviewed in the superadmin). Shipping those notes would be the opposite of the
 * content-honesty rule, so they are deliberately not ported. The one status note
 * that IS true — the seeded evaluation accounts — is kept below, and it renders as
 * a plain `.status-note` (not `.is-placeholder`, whose ::before stamps
 * "Placeholder — " on the line: those accounts really do sign you in).
 *
 * The demo accounts themselves are a platform fixture, not site content, so they
 * are exported separately and are NOT editable in the website editor. */
const partnerAccount = {
  meta: {
    title: "Partner Account | Lugano Home Collection",
    description: "Sign in to your LHC partner account, or apply for a trade account as a dealer, agent, architect, developer or contract client.",
    image: "https://picsum.photos/seed/lhc-og-login-101/1200/630",
  },
  visual: {
    image: "https://picsum.photos/seed/lhc-account-visual-1/1200/1600",
    imageAlt: "Placeholder: LHC showroom or project photography",
    eyebrow: "Partner Account",
    title: "Working with LHC.",
    bodyBefore: "A partner account gives trade professionals access to pricing, order status and project files. Dealers also get access to ",
    bodyStrong: "LHC Studio",
    bodyAfter: ", the kitchen design, quoting and manufacturing platform.",
    who: ["Dealers", "Agents", "Architects", "Interior Designers", "Developers", "Contract & Hospitality"],
  },
  tabs: { signin: "Sign In", apply: "Apply for an Account" },
  signin: {
    title: "Sign in to your account.",
    sub: "For existing LHC dealers, agents and trade partners.",
    email: "Email",
    password: "Password",
    remember: "Keep me signed in",
    forgot: "Forgot password",
    forgotHref: "contact.html",
    button: "Sign In",
    busy: "Signing in…",
    error: "Please enter your email and password.",
    footBefore: "Don't have a partner account yet? ",
    footLink: "Apply for one",
    footAfter: ".",
  },
  demo: {
    label: "Demo accounts",
    note: "prepared accounts for evaluation — one click signs you in.",
  },
  apply: {
    title: "Apply for a partner account.",
    sub: "Trade accounts are reviewed by LHC before activation. Tell us about your business and we'll be in touch.",
    typeLabel: "I am applying as a",
    types: [
      { id: "dealer", title: "Dealer / Retailer", desc: "Showroom or retail partner selling LHC.", dealerFields: true },
      { id: "agent", title: "Agent", desc: "Representing LHC in a territory or market.", dealerFields: true },
      { id: "architect", title: "Architect / Interior Designer", desc: "Specifying LHC on client projects.", dealerFields: false },
      { id: "developer", title: "Developer", desc: "Multi-unit residential or mixed-use projects.", dealerFields: false },
      { id: "contract", title: "Contract / Hospitality", desc: "Hotels, restaurants and commercial fit-outs.", dealerFields: false },
      { id: "other", title: "Other Trade", desc: "Tell us more in the message below.", dealerFields: false },
    ],
    fields: {
      first: "First Name", last: "Last Name", email: "Work Email", phone: "Phone",
      company: "Company", website: "Website", websitePlaceholder: "https://",
      country: "Country", city: "City",
      showroom: "Showroom", showroomSelect: "Select…", showroomOptions: ["Yes, existing showroom", "Planned showroom", "No showroom"],
      markets: "Markets Served", marketsPlaceholder: "Countries / regions",
      message: "About your business", messagePlaceholder: "Project types, brands you currently represent, typical volumes…",
    },
    consent: "I agree to be contacted by LHC regarding this application",
    button: "Submit Application",
    busy: "Sending…",
    error: "Please complete the required fields and select an account type.",
    sendError: "We couldn't send your application — please try again.",
    footBefore: "Already a partner? ",
    footLink: "Sign in",
    footAfter: ".",
    /* the exact confirmation the platform has always shown — do not reword */
    thanks: "Thank you for applying. We will contact you soon.",
  },
};
export default partnerAccount;

/** the platform's seeded evaluation accounts (same set the old login screen offered) */
export const DEMO_ACCOUNTS = [
  { label: "Superadmin", email: "superadmin@lhc.test", password: "Admin123!", tenant: "Platform" },
  { label: "Sales CRM (Platform)", email: "sales.global@lhc.test", password: "Sales123!", tenant: "LHC Global Sales" },
  { label: "Owner", email: "dealerowner@lhc.test", password: "Owner123!", tenant: "New Home" },
  { label: "Designer", email: "designer@lhc.test", password: "Design123!", tenant: "New Home" },
  { label: "Catalog Technician", email: "catalog@lhc.test", password: "Catalog123!", tenant: "LHC Studio" },
];
export const demoKey = (email) => email.split("@")[0].replace(/\./g, "-");
